import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Mail, MapPin, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { BRAND, CONTACT } from "../data/mock";
import { FadeIn, SplitText, HexPattern, Eyebrow } from "../components/reactbits/Anim";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTEREST_OPTIONS = [
  "Design-partner programme",
  "Product walkthrough",
  "Security questionnaire",
  "Careers",
  "Other",
];

const initialForm = { name: "", email: "", company: "", interest: "", message: "", consent: false, website: "" };

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!form.consent) {
      toast.error("Please agree to the MedHive Privacy Policy.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        interest: form.interest,
        message: form.message.trim(),
        consent: form.consent,
        website: form.website,
      });
      setForm(initialForm);
      setSent(true);
      toast.success("Message sent! Our team will reach out shortly.");
    } catch (err) {
      toast.error("Something went wrong sending your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white text-[#1F1B13]" data-testid="contact-page">
      <section className="relative overflow-hidden bg-[#FDFBF7] pt-40 pb-24">
        <HexPattern opacity={0.06} />
        <div
          className="pointer-events-none absolute -top-32 left-1/2 h-[420px] w-[760px] -translate-x-1/2 rounded-full"
          style={{ background: "radial-gradient(ellipse at center, rgba(240,163,34,0.13), transparent 60%)" }}
        />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid gap-16 lg:grid-cols-2">
            {/* Left copy */}
            <div>
              <Eyebrow>contact us</Eyebrow>
              <h1 className="mt-6 font-heading text-4xl font-bold leading-tight text-[#1F1B13] sm:text-6xl" data-testid="contact-heading">
                <SplitText text="Leave a" /> <span className="text-[#E8930C]"><SplitText text="Message" delay={0.2} /></span>
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="mt-6 max-w-md text-base leading-relaxed text-[#5D564A] sm:text-lg"
              >
                {CONTACT.body}
              </motion.p>

              <FadeIn delay={0.5}>
                <div className="mt-12 space-y-4">
                  <a
                    href={`mailto:${BRAND.email}`}
                    className="flex items-center gap-4 rounded-2xl border border-[#EDE5D3] bg-white p-5 shadow-[0_2px_10px_rgba(31,27,19,0.03)] transition-colors duration-300 hover:border-[#F0A322]/50"
                  >
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#F0A322]/[0.14]">
                      <Mail className="h-5 w-5 text-[#B45309]" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-[#8A8272]">Email</p>
                      <p className="text-sm font-semibold text-[#37311F]">{BRAND.email}</p>
                    </div>
                  </a>
                  <div className="flex items-center gap-4 rounded-2xl border border-[#EDE5D3] bg-white p-5 shadow-[0_2px_10px_rgba(31,27,19,0.03)]">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#F0A322]/[0.14]">
                      <MapPin className="h-5 w-5 text-[#B45309]" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-[#8A8272]">Location</p>
                      <p className="text-sm font-semibold text-[#37311F]">{BRAND.location}</p>
                    </div>
                  </div>
                </div>
              </FadeIn>
            </div>

            {/* Form */}
            <FadeIn delay={0.25}>
              {sent ? (
                <div className="rounded-3xl border border-[#F0A322]/40 bg-[#F0A322]/[0.07] p-12 text-center" data-testid="contact-success">
                  <Send className="mx-auto h-10 w-10 text-[#E8930C]" />
                  <p className="mt-5 font-heading text-2xl font-semibold text-[#1F1B13]">Thanks, your request has been sent.</p>
                  <p className="mt-2 text-sm text-[#5D564A]">We read every enquiry and reply as quickly as we can.</p>
                  <button
                    onClick={() => setSent(false)}
                    className="mt-7 rounded-full border border-[#D9D0BC] px-6 py-2.5 text-sm font-semibold text-[#5D564A] transition-colors hover:border-[#E8930C] hover:text-[#B45309]"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="rounded-3xl border border-[#EDE5D3] bg-white p-8 shadow-[0_8px_40px_rgba(31,27,19,0.06)] sm:p-10"
              >
                <div className="grid gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-[#4A4436]">Full Name *</Label>
                    <Input
                      id="name"
                      data-testid="contact-input-name"
                      value={form.name}
                      onChange={set("name")}
                      placeholder="Jane Doe"
                      className="border-[#E5DCC9] bg-[#FDFBF7] text-[#1F1B13] placeholder:text-[#B8AE9C] focus-visible:ring-[#E8930C]/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#4A4436]">Work Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="contact-input-email"
                      value={form.email}
                      onChange={set("email")}
                      placeholder="jane@company.com"
                      className="border-[#E5DCC9] bg-[#FDFBF7] text-[#1F1B13] placeholder:text-[#B8AE9C] focus-visible:ring-[#E8930C]/50"
                    />
                  </div>
                </div>
                <div className="mt-6 grid gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-[#4A4436]">Company</Label>
                    <Input
                      id="company"
                      data-testid="contact-input-company"
                      value={form.company}
                      onChange={set("company")}
                      placeholder="Your organization"
                      className="border-[#E5DCC9] bg-[#FDFBF7] text-[#1F1B13] placeholder:text-[#B8AE9C] focus-visible:ring-[#E8930C]/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#4A4436]">I am interested in…</Label>
                    <Select value={form.interest} onValueChange={(v) => setForm((f) => ({ ...f, interest: v }))}>
                      <SelectTrigger data-testid="contact-input-interest" className="border-[#E5DCC9] bg-[#FDFBF7] text-[#1F1B13] focus:ring-[#E8930C]/50">
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                      <SelectContent className="border-[#EDE5D3] bg-white text-[#1F1B13]">
                        {INTEREST_OPTIONS.map((o) => (
                          <SelectItem key={o} value={o}>{o}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {/* honeypot - hidden from humans */}
                <input type="text" value={form.website} onChange={set("website")} name="website" tabIndex="-1" autoComplete="off" aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }} />
                <div className="mt-6 space-y-2">
                  <Label htmlFor="message" className="text-[#4A4436]">Message *</Label>
                  <Textarea
                    id="message"
                    rows={5}
                    data-testid="contact-input-message"
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Tell us about your medical device R&D goals..."
                    className="border-[#E5DCC9] bg-[#FDFBF7] text-[#1F1B13] placeholder:text-[#B8AE9C] focus-visible:ring-[#E8930C]/50"
                  />
                </div>
                <div className="mt-6 flex items-center gap-3">
                  <Checkbox
                    id="consent"
                    data-testid="contact-consent"
                    checked={form.consent}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, consent: !!v }))}
                    className="border-[#C9BFA8] data-[state=checked]:border-[#E8930C] data-[state=checked]:bg-[#E8930C] data-[state=checked]:text-white"
                  />
                  <Label htmlFor="consent" className="cursor-pointer text-sm font-normal text-[#5D564A]">
                    {CONTACT.consent}
                  </Label>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="group mt-8 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-[#F0A322] px-8 py-4 text-sm font-semibold uppercase tracking-wide text-[#231A07] transition-colors duration-200 hover:bg-[#E8930C] disabled:opacity-60"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />}
                  {CONTACT.button}
                </button>
              </form>
              )}
            </FadeIn>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
